import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AppLayout } from '@/app/layouts/AppLayout';
import { AuthLayout } from '@/app/layouts/AuthLayout';
import { OnboardingLayout } from '@/app/layouts/OnboardingLayout';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { AuthRedirect } from '@/features/auth/components/AuthRedirect';
import { ROUTES } from '@/shared/config/routes';

// Lazy-loaded feature pages
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const SignupPage = lazy(() => import('@/features/auth/SignupPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/ForgotPasswordPage'));
const AuthCallbackPage = lazy(() => import('@/features/auth/components/AuthCallback'));
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'));
const OnboardingPage = lazy(() => import('@/features/onboarding/OnboardingPage'));
const ProcessListPage = lazy(() => import('@/features/processes/components/ProcessListPage'));
const ProcessDetailPage = lazy(() => import('@/features/processes/components/ProcessDetailPage'));
const ProcessCreatePage = lazy(() => import('@/features/processes/components/ProcessCreatePage'));
const ProcessEditPage = lazy(() => import('@/features/processes/components/ProcessEditPage'));
const DocumentListPage = lazy(() => import('@/features/documents/components/DocumentListPage'));
const DocumentDetailPage = lazy(() => import('@/features/documents/components/DocumentDetailPage'));
const DocumentCreatePage = lazy(() => import('@/features/documents/components/DocumentCreatePage'));
const DocumentEditPage = lazy(() => import('@/features/documents/components/DocumentEditPage'));
const NcrListPage = lazy(() => import('@/features/ncr/components/NcrListPage'));
const NcrDetailPage = lazy(() => import('@/features/ncr/components/NcrDetailPage'));
const NcrCreatePage = lazy(() => import('@/features/ncr/components/NcrCreatePage'));
const NcrEditPage = lazy(() => import('@/features/ncr/components/NcrEditPage'));
const KpiPage = lazy(() => import('@/features/kpi/components/KpiPage'));
const QmsPlanningPage = lazy(() => import('@/features/qms-planning/components/QmsPlanningPage'));
const OrganizationPage = lazy(() => import('@/features/organization/components/OrganizationPage'));
const RiskListPage = lazy(() => import('@/features/risks/components/RiskListPage'));
const RiskDetailPage = lazy(() => import('@/features/risks/components/RiskDetailPage'));
const RiskCreatePage = lazy(() => import('@/features/risks/components/RiskCreatePage'));
const RiskEditPage = lazy(() => import('@/features/risks/components/RiskEditPage'));
const AuditListPage = lazy(() => import('@/features/audits/components/AuditListPage'));
const AuditDetailPage = lazy(() => import('@/features/audits/components/AuditDetailPage'));
const AuditCreatePage = lazy(() => import('@/features/audits/components/AuditCreatePage'));
const AuditEditPage = lazy(() => import('@/features/audits/components/AuditEditPage'));
const TemplateListPage = lazy(() => import('@/features/audits/components/TemplateListPage'));
const TemplateCreatePage = lazy(() => import('@/features/audits/components/TemplateCreatePage'));
const TemplateEditPage = lazy(() => import('@/features/audits/components/TemplateEditPage'));
const TrainingPage = lazy(() => import('@/features/training/components/TrainingPage'));
const TrainingEventDetailPage = lazy(() => import('@/features/training/components/EventDetailPage'));
const LessonsLearnedPage = lazy(() => import('@/features/lessons-learned/components/LessonsLearnedPage'));
const SettingsPage = lazy(() => import('@/features/settings/components/SettingsPage'));
const SupplierListPage = lazy(() => import('@/features/suppliers/components/SupplierListPage'));
const SupplierDetailPage = lazy(() => import('@/features/suppliers/components/SupplierDetailPage'));
const SupplierCreatePage = lazy(() => import('@/features/suppliers/components/SupplierCreatePage'));
const SupplierEditPage = lazy(() => import('@/features/suppliers/components/SupplierEditPage'));
const EquipmentListPage = lazy(() => import('@/features/equipment/components/EquipmentListPage'));
const EquipmentDetailPage = lazy(() => import('@/features/equipment/components/EquipmentDetailPage'));
const EquipmentCreatePage = lazy(() => import('@/features/equipment/components/EquipmentCreatePage'));
const EquipmentEditPage = lazy(() => import('@/features/equipment/components/EquipmentEditPage'));
const InspectionListPage = lazy(() => import('@/features/inspections/components/InspectionListPage'));
const InspectionPlanDetailPage = lazy(() => import('@/features/inspections/components/InspectionPlanDetailPage'));
const InspectionPlanCreatePage = lazy(() => import('@/features/inspections/components/InspectionPlanCreatePage'));
const InspectionPlanEditPage = lazy(() => import('@/features/inspections/components/InspectionPlanEditPage'));
const InspectionRecordDetailPage = lazy(() => import('@/features/inspections/components/InspectionRecordDetail'));
const ChangeListPage = lazy(() => import('@/features/change-management/components/ChangeListPage'));
const ChangeDetailPage = lazy(() => import('@/features/change-management/components/ChangeDetailPage'));
const ChangeCreatePage = lazy(() => import('@/features/change-management/components/ChangeCreatePage'));
const ChangeEditPage = lazy(() => import('@/features/change-management/components/ChangeEditPage'));
const ComplaintListPage = lazy(() => import('@/features/complaints/components/ComplaintListPage'));
const ComplaintCreatePage = lazy(() => import('@/features/complaints/components/ComplaintCreatePage'));
const ComplaintDetailPage = lazy(() => import('@/features/complaints/components/ComplaintDetailPage'));
const ComplaintEditPage = lazy(() => import('@/features/complaints/components/ComplaintEditPage'));
const DesignProjectListPage = lazy(() => import('@/features/design-dev/components/DesignProjectListPage'));
const DesignProjectCreatePage = lazy(() => import('@/features/design-dev/components/DesignProjectCreatePage'));
const DesignProjectDetailPage = lazy(() => import('@/features/design-dev/components/DesignProjectDetailPage'));
const DesignProjectEditPage = lazy(() => import('@/features/design-dev/components/DesignProjectEditPage'));
const ReviewListPage = lazy(() => import('@/features/management-reviews/components/ReviewListPage'));
const ReviewCreatePage = lazy(() => import('@/features/management-reviews/components/ReviewCreatePage'));
const ReviewDetailPage = lazy(() => import('@/features/management-reviews/components/ReviewDetailPage'));
const ReviewEditPage = lazy(() => import('@/features/management-reviews/components/ReviewEditPage'));
const AiAssistantPage = lazy(() => import('@/features/ai-assistant/components/AiAssistantPage'));
const ReportListPage = lazy(() => import('@/features/reports/components/ReportListPage'));

// Loading fallback
function PageSkeleton() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        {/* Auth routes — redirect to dashboard if already authenticated */}
        <Route element={<AuthRedirect />}>
          <Route element={<AuthLayout />}>
            <Route path={ROUTES.AUTH.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.AUTH.SIGNUP} element={<SignupPage />} />
            <Route path={ROUTES.AUTH.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
          </Route>
        </Route>

        {/* Auth callback — handles email confirmation & password reset redirects */}
        <Route path={ROUTES.AUTH.CALLBACK} element={<AuthCallbackPage />} />

        {/* Password reset — needs auth session but must bypass onboarding guard */}
        <Route element={<AuthLayout />}>
          <Route path={ROUTES.AUTH.RESET_PASSWORD} element={<ResetPasswordPage />} />
        </Route>

        {/* Protected routes — require authentication */}
        <Route element={<ProtectedRoute />}>
          {/* Onboarding routes */}
          <Route path={ROUTES.ONBOARDING.ROOT} element={<OnboardingLayout />}>
            <Route index element={<OnboardingPage />} />
          </Route>

          {/* App routes */}
          <Route element={<AppLayout />}>
            <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />

            {/* Process routes */}
            <Route path={ROUTES.PROCESSES} element={<ProcessListPage />} />
            <Route path={`${ROUTES.PROCESSES}/new`} element={<ProcessCreatePage />} />
            <Route path={`${ROUTES.PROCESSES}/:id`} element={<ProcessDetailPage />} />
            <Route path={`${ROUTES.PROCESSES}/:id/edit`} element={<ProcessEditPage />} />

            {/* Document routes */}
            <Route path={ROUTES.DOCUMENTS} element={<DocumentListPage />} />
            <Route path={`${ROUTES.DOCUMENTS}/new`} element={<DocumentCreatePage />} />
            <Route path={`${ROUTES.DOCUMENTS}/:id`} element={<DocumentDetailPage />} />
            <Route path={`${ROUTES.DOCUMENTS}/:id/edit`} element={<DocumentEditPage />} />

            {/* Quality Issues (NCR) routes */}
            <Route path={ROUTES.QUALITY_ISSUES} element={<NcrListPage />} />
            <Route path={`${ROUTES.QUALITY_ISSUES}/new`} element={<NcrCreatePage />} />
            <Route path={`${ROUTES.QUALITY_ISSUES}/:id`} element={<NcrDetailPage />} />
            <Route path={`${ROUTES.QUALITY_ISSUES}/:id/edit`} element={<NcrEditPage />} />

            {/* KPI routes */}
            <Route path={ROUTES.KPIS} element={<KpiPage />} />

            {/* Organization routes */}
            <Route path={ROUTES.ORG_STRUCTURE} element={<OrganizationPage />} />
            <Route path={ROUTES.JOB_ROLES} element={<OrganizationPage />} />
            <Route path={ROUTES.STAFF} element={<OrganizationPage />} />
            <Route path={ROUTES.ORG_CHART} element={<OrganizationPage />} />

            {/* Risk Register routes */}
            <Route path={ROUTES.RISKS} element={<RiskListPage />} />
            <Route path={`${ROUTES.RISKS}/new`} element={<RiskCreatePage />} />
            <Route path={`${ROUTES.RISKS}/:id`} element={<RiskDetailPage />} />
            <Route path={`${ROUTES.RISKS}/:id/edit`} element={<RiskEditPage />} />

            {/* Internal Audits routes */}
            <Route path={ROUTES.AUDITS} element={<AuditListPage />} />
            <Route path={`${ROUTES.AUDITS}/new`} element={<AuditCreatePage />} />
            <Route path={ROUTES.AUDIT_TEMPLATES} element={<TemplateListPage />} />
            <Route path={`${ROUTES.AUDIT_TEMPLATES}/new`} element={<TemplateCreatePage />} />
            <Route path={`${ROUTES.AUDIT_TEMPLATES}/:id`} element={<TemplateEditPage />} />
            <Route path={`${ROUTES.AUDITS}/:id`} element={<AuditDetailPage />} />
            <Route path={`${ROUTES.AUDITS}/:id/edit`} element={<AuditEditPage />} />

            {/* Training routes */}
            <Route path={ROUTES.TRAINING} element={<Navigate to={ROUTES.TRAINING_COURSES} replace />} />
            <Route path={ROUTES.TRAINING_COURSES} element={<TrainingPage />} />
            <Route path={ROUTES.TRAINING_REQUIREMENTS} element={<TrainingPage />} />
            <Route path={ROUTES.TRAINING_EVENTS} element={<TrainingPage />} />
            <Route path={`${ROUTES.TRAINING_EVENTS}/:id`} element={<TrainingEventDetailPage />} />
            <Route path={ROUTES.TRAINING_CALENDAR} element={<TrainingPage />} />
            <Route path={ROUTES.TRAINING_COMPLIANCE} element={<TrainingPage />} />

            {/* Supplier routes */}
            <Route path={ROUTES.SUPPLIERS} element={<SupplierListPage />} />
            <Route path={`${ROUTES.SUPPLIERS}/new`} element={<SupplierCreatePage />} />
            <Route path={`${ROUTES.SUPPLIERS}/:id`} element={<SupplierDetailPage />} />
            <Route path={`${ROUTES.SUPPLIERS}/:id/edit`} element={<SupplierEditPage />} />

            {/* Equipment routes */}
            <Route path={ROUTES.EQUIPMENT} element={<EquipmentListPage />} />
            <Route path={`${ROUTES.EQUIPMENT}/new`} element={<EquipmentCreatePage />} />
            <Route path={`${ROUTES.EQUIPMENT}/:id`} element={<EquipmentDetailPage />} />
            <Route path={`${ROUTES.EQUIPMENT}/:id/edit`} element={<EquipmentEditPage />} />

            {/* Inspection routes */}
            <Route path={ROUTES.INSPECTIONS} element={<InspectionListPage />} />
            <Route path={`${ROUTES.INSPECTIONS}/plans/new`} element={<InspectionPlanCreatePage />} />
            <Route path={`${ROUTES.INSPECTIONS}/plans/:id`} element={<InspectionPlanDetailPage />} />
            <Route path={`${ROUTES.INSPECTIONS}/plans/:id/edit`} element={<InspectionPlanEditPage />} />
            <Route path={`${ROUTES.INSPECTIONS}/records/:id`} element={<InspectionRecordDetailPage />} />

            {/* Change Management routes */}
            <Route path={ROUTES.CHANGE_MANAGEMENT} element={<ChangeListPage />} />
            <Route path={`${ROUTES.CHANGE_MANAGEMENT}/new`} element={<ChangeCreatePage />} />
            <Route path={`${ROUTES.CHANGE_MANAGEMENT}/:id`} element={<ChangeDetailPage />} />
            <Route path={`${ROUTES.CHANGE_MANAGEMENT}/:id/edit`} element={<ChangeEditPage />} />

            {/* Complaints routes */}
            <Route path={ROUTES.COMPLAINTS} element={<ComplaintListPage />} />
            <Route path={`${ROUTES.COMPLAINTS}/new`} element={<ComplaintCreatePage />} />
            <Route path={`${ROUTES.COMPLAINTS}/:id`} element={<ComplaintDetailPage />} />
            <Route path={`${ROUTES.COMPLAINTS}/:id/edit`} element={<ComplaintEditPage />} />

            {/* Design & Development routes */}
            <Route path={ROUTES.DESIGN_DEV} element={<DesignProjectListPage />} />
            <Route path={`${ROUTES.DESIGN_DEV}/new`} element={<DesignProjectCreatePage />} />
            <Route path={`${ROUTES.DESIGN_DEV}/:id`} element={<DesignProjectDetailPage />} />
            <Route path={`${ROUTES.DESIGN_DEV}/:id/edit`} element={<DesignProjectEditPage />} />

            {/* Management Reviews routes */}
            <Route path={ROUTES.MANAGEMENT_REVIEWS} element={<ReviewListPage />} />
            <Route path={`${ROUTES.MANAGEMENT_REVIEWS}/new`} element={<ReviewCreatePage />} />
            <Route path={`${ROUTES.MANAGEMENT_REVIEWS}/:id`} element={<ReviewDetailPage />} />
            <Route path={`${ROUTES.MANAGEMENT_REVIEWS}/:id/edit`} element={<ReviewEditPage />} />

            {/* AI Assistant */}
            <Route path={ROUTES.AI_ASSISTANT} element={<AiAssistantPage />} />

            {/* Reports */}
            <Route path={ROUTES.REPORTS} element={<ReportListPage />} />

            {/* Lessons Learned routes */}
            <Route path={ROUTES.LESSONS_LEARNED} element={<LessonsLearnedPage />} />

            {/* QMS Planning routes */}
            <Route path={ROUTES.QMS_PLANNING} element={<QmsPlanningPage />} />

            {/* Settings routes */}
            <Route path={ROUTES.SETTINGS.ROOT} element={<SettingsPage />} />
            <Route path={ROUTES.SETTINGS.GENERAL} element={<SettingsPage />} />
            <Route path={ROUTES.SETTINGS.USERS} element={<SettingsPage />} />
            <Route path={ROUTES.SETTINGS.CONFIGURATION} element={<SettingsPage />} />
            <Route path={ROUTES.SETTINGS.AUDIT_LOGS} element={<SettingsPage />} />
            <Route path={ROUTES.SETTINGS.NOTIFICATIONS} element={<SettingsPage />} />
            <Route path={ROUTES.SETTINGS.AI} element={<SettingsPage />} />
            <Route path={ROUTES.SETTINGS.BILLING} element={<SettingsPage />} />
            <Route path={ROUTES.SETTINGS.DATA_IMPORT} element={<SettingsPage />} />

            {/* Catch-all redirect to dashboard */}
            <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
